import React, { useEffect, useState } from "react";
import { Button, Spin, Tabs, Tooltip } from "antd";
import {
  ReloadOutlined,
  AlertOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  FiClipboard,
  FiMapPin,
  FiCheckSquare,
  FiFlag,
  FiEye,
  FiEdit2,
  FiUsers,
  FiClock,
  FiInfo,
} from "react-icons/fi";
import "./ProjectHome.scss";
import "../dashboard/Dashboard.scss";
import { RolesEnum } from "../../../services/rolesEnum";
import { isAuthorized } from "../../../services/authService";
import { escalateStalledSiteVisits } from "../../../services/features/site.service";
import { httpService } from "../../../services/httpService.service";
import { formatDateAndTime } from "../../../services/helpFunctions/formatDateAndTime";
import MessagePopUp from "../../components/Messages/MessagePopUp";
import doneIcon from "../../../Assets/svg/done.svg";
import errorIcon from "../../../Assets/svg/exclamationMark.svg";
import EditProject from "../projects/EditProject";
import PreviewProject from "../projects/PreviewProject";
import AddSiteVisitCardModal from "../planning/VisitBoard/Modals/AddSiteVisitCardModal";
import AddNewTaskModal from "../planning/TasksAssignments/Modals/AddNewTaskModal";
import AddIpPlan from "../planning/modals/AddModal";
import AddRedFlagCardModal from "../Flags/Modals/AddRedFlagCardModal";
import TpmSummary from "../dashboard/TPMsummary/TpmSummary";
import IpSummary from "../dashboard/IPsummary/IpSummary";
import VisitDataSummary from "../dashboard/VisitDataSummary/VisitDataSummary";
import TaskQueuePanel from "./panels/TaskQueuePanel";
import UpcomingVisitsPanel from "./panels/UpcomingVisitsPanel";
import VisitsByStagePanel from "./panels/VisitsByStagePanel";
import FlagsSummaryPanel from "./panels/FlagsSummaryPanel";
import QuickLinksPanel from "./panels/QuickLinksPanel";
import ProjectHubPanel from "./panels/ProjectHubPanel";
import PmHomePanel from "./panels/PmHomePanel";
import CoordinatorHomePanel from "./panels/CoordinatorHomePanel";
import ReportsPanel from "./reports/ReportsPanel";
import StalledThresholdsModal from "../../components/StalledThresholds/StalledThresholdsModal";

const { TabPane } = Tabs;

// F-01 — Role-based home screen, now the single project landing page: it merges
// the former Dashboard (project header, quick actions, summary tabs) with the
// role-based panels. The old /project/dashboard route redirects here.
const ProjectHome = () => {
  const projectID = localStorage.getItem("currentProjectID");
  const isGuestShare = process.env.REACT_APP_API_IS_GUEST;
  const [reloadKey, setReloadKey] = useState(0);
  const [escalating, setEscalating] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [loadingProject, setLoadingProject] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showThresholdsModal, setShowThresholdsModal] = useState(false);
  const [showPreviewProjectModal, setShowPreviewProjectModal] = useState(false);
  const [isModalVisibleSiteVisit, setIsModalVisibleSiteVisit] = useState(false);
  const [isModalVisibleAddTask, setIsModalVisibleAddTask] = useState(false);
  const [isModalVisibleAddFlag, setIsModalVisibleAddFlag] = useState(false);
  const [isModalVisibleAddModal, setIsModalVisibleAddModal] = useState(false);
  const [msg, setMsg] = useState({
    trigger: false,
    type: "",
    icon: "",
    title: "",
    text: "",
  });

  const username = localStorage.getItem("username");
  const roleName = localStorage.getItem("role");

  const isPM = isAuthorized([RolesEnum.ProjectManager, RolesEnum.SystemAdmin]);
  const isCoordinator = isAuthorized([RolesEnum.FieldCoordinator]);
  const isLogistics = isAuthorized([RolesEnum.FieldLogistics]);
  const isAnalyst = isAuthorized([RolesEnum.DataAnalyst]);

  const notify = (type, title, text) => {
    setMsg({
      trigger: true,
      type,
      icon: type === "success" ? doneIcon : errorIcon,
      title,
      text,
    });
    setTimeout(() => setMsg((m) => ({ ...m, trigger: false })), 3000);
  };

  const fetchProjectData = async () => {
    if (!projectID) return;
    setLoadingProject(true);
    try {
      await new Promise((resolve, reject) => {
        httpService.get(
          `/api/Projects?id=${projectID}`,
          (response) => {
            setProjectData(response.data);
            resolve();
          },
          (error) => reject(error)
        );
      });
    } catch (error) {
      // error surfaced elsewhere
    } finally {
      setLoadingProject(false);
    }
  };

  const fetchDashboardData = () => {
    if (!projectID) return;
    setLoadingDashboard(true);
    httpService.get(
      `/api/Projects/getProjectInfo?id=${projectID}`,
      (response) => {
        setDashboardData(response.data);
        setLoadingDashboard(false);
      },
      () => setLoadingDashboard(false)
    );
  };

  useEffect(() => {
    fetchDashboardData();
    fetchProjectData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const refreshAll = () => setReloadKey((k) => k + 1);

  // F-03 — PM manual trigger to flag stalled site visits.
  const handleEscalate = () => {
    setEscalating(true);
    escalateStalledSiteVisits(
      () => {
        setEscalating(false);
        notify(
          "success",
          "Stalled visits refreshed",
          "Stalled visits have been re-evaluated."
        );
        refreshAll();
      },
      () => {
        setEscalating(false);
        notify("danger", "Refresh failed", "Couldn't refresh stalled visits.");
      }
    );
  };

  const handleCancelModals = () => {
    setIsModalVisibleSiteVisit(false);
    setIsModalVisibleAddTask(false);
    setIsModalVisibleAddFlag(false);
    setIsModalVisibleAddModal(false);
  };

  // Header chips: the clients and when the round was last touched. Resolved once
  // here rather than inline, since the header is now one line of chips.
  const clientNames = (dashboardData?.clients || [])
    .map((c) => c.name)
    .filter(Boolean)
    .join(", ");
  const updatedLabel = dashboardData?.updatedDate
    ? formatDateAndTime(dashboardData.updatedDate)
    : dashboardData?.createdDate
    ? formatDateAndTime(dashboardData.createdDate)
    : "-";

  const renderRoleHome = () => {
    // F-01 — PM and Coordinator land on the aggregated home endpoints
    // (pmHome / coordinatorHome). PmHomePanel already carries project health,
    // the stalled overview, flag patterns and IP completeness, so the older
    // IPCompletenessPanel / FlagsSummaryPanel are not repeated underneath it.
    if (isPM) {
      return (
        <>
          <PmHomePanel />
          <ProjectHubPanel />
          <VisitsByStagePanel />
          <TaskQueuePanel mode="team" />
        </>
      );
    }
    if (isCoordinator) {
      return (
        <>
          <CoordinatorHomePanel />
          <ProjectHubPanel />
          <div className="homeRow2">
            <UpcomingVisitsPanel />
            <FlagsSummaryPanel />
          </div>
          <TaskQueuePanel mode="personal" />
        </>
      );
    }
    if (isAnalyst) {
      return (
        <>
          <VisitsByStagePanel />
          <TaskQueuePanel mode="personal" />
          <QuickLinksPanel />
        </>
      );
    }
    if (isLogistics) {
      return (
        <>
          <UpcomingVisitsPanel />
          <TaskQueuePanel mode="personal" />
          <QuickLinksPanel />
        </>
      );
    }
    return (
      <>
        <TaskQueuePanel mode="personal" />
        <QuickLinksPanel />
      </>
    );
  };

  if (loadingDashboard && !dashboardData) {
    return (
      <div
        style={{
          height: "calc(100vh - 130px)",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="small" />
      </div>
    );
  }

  return (
    <div className="projectHome">
      <MessagePopUp
        trigger={msg.trigger}
        type={msg.type}
        icon={msg.icon}
        messageTitle={msg.title}
        messageText={msg.text}
      />

      {/* Compact header strip. What used to be a 140px pink band with a title,
          a description paragraph and three tag pills is one line: who you are,
          which round, and the two project actions. The round's detail lives in
          the chips' tooltips instead of taking vertical room. */}
      <div className="homeHeader">
        <div className="homeHeaderMain">
          <h2 className="homeHeaderTitle">
            {username
              ? `Welcome back, ${username}`
              : dashboardData?.projectName || "Project Home"}
          </h2>
          <span className="homeHeaderMeta">
            {[dashboardData?.projectName, roleName].filter(Boolean).join(" · ")}
          </span>
        </div>

        <div className="homeHeaderChips">
          <Tooltip title={`Clients: ${clientNames || "-"}`}>
            <span className="homeChip">
              <FiUsers />
              <span className="homeChipText">{clientNames || "-"}</span>
            </span>
          </Tooltip>
          <Tooltip title={`Last updated: ${updatedLabel}`}>
            <span className="homeChip">
              <FiClock />
              <span className="homeChipText">{updatedLabel}</span>
            </span>
          </Tooltip>
          {dashboardData?.description && (
            <Tooltip title={dashboardData.description}>
              <span className="homeChip">
                <FiInfo />
                <span className="homeChipText">About this round</span>
              </span>
            </Tooltip>
          )}
        </div>

        <div className="homeHeaderActions">
          <Tooltip title="Preview project details">
            <Button
              className="homeIconBtn"
              onClick={async () => {
                setShowPreviewProjectModal(true);
                if (!projectData) await fetchProjectData();
              }}
            >
              <FiEye />
            </Button>
          </Tooltip>
          {isPM && (
            <Tooltip title="Edit project">
              <Button
                className="homeIconBtn"
                onClick={() => setShowEditProjectModal(true)}
              >
                <FiEdit2 />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Everything below the header is organised into tabs. */}
      <div className="planning-page homeTabs">
        <Tabs
          type="card"
          className="card-tabs"
          destroyInactiveTabPane
          defaultActiveKey="home"
          tabBarExtraContent={{
            right: (
              <div className="homeTabActions">
                {isPM && (
                  <Tooltip title="Re-check for stalled site visits and raise flags where needed">
                    <Button
                      type="text"
                      icon={<AlertOutlined />}
                      loading={escalating}
                      onClick={handleEscalate}
                      className="homeTabActionBtn"
                    >
                      Escalate stalled
                    </Button>
                  </Tooltip>
                )}
                {/* F-03 — configure how many idle days count as stalled. */}
                {isPM && (
                  <Tooltip title="Configure the stalled thresholds per task type">
                    <Button
                      type="text"
                      icon={<SettingOutlined />}
                      onClick={() => setShowThresholdsModal(true)}
                      className="homeTabActionBtn"
                    >
                      Stalled thresholds
                    </Button>
                  </Tooltip>
                )}
                <Tooltip title="Refresh this page">
                  <Button
                    type="text"
                    icon={<ReloadOutlined />}
                    onClick={refreshAll}
                    className="homeTabActionBtn"
                  />
                </Tooltip>
              </div>
            ),
          }}
        >
          <TabPane tab="Home" key="home">
            <div className="insideTab">
              {/* Quick actions: one compact row of 32px chips instead of a
                  bordered card of 52px image buttons. Same four actions. */}
              {!isGuestShare && (
                <div className="homeQuickBar">
                  <span className="homeQuickBarLabel">New</span>
                  {isPM && (
                    <Button
                      className="homeQuickBtn"
                      onClick={() => setIsModalVisibleAddModal(true)}
                    >
                      <FiClipboard /> IP planning card
                    </Button>
                  )}
                  {isPM && (
                    <Button
                      className="homeQuickBtn"
                      onClick={() => setIsModalVisibleSiteVisit(true)}
                    >
                      <FiMapPin /> Site visit card
                    </Button>
                  )}
                  <Button
                    className="homeQuickBtn"
                    onClick={() => setIsModalVisibleAddTask(true)}
                  >
                    <FiCheckSquare /> Task card
                  </Button>
                  <Button
                    className="homeQuickBtn"
                    onClick={() => setIsModalVisibleAddFlag(true)}
                  >
                    <FiFlag /> Red flag card
                  </Button>
                </div>
              )}

              {/* Generate Reports — monitoring reports for government/donor
                  oversight. Shown to everyone (incl. guest share) since it is
                  read-only. */}
              <ReportsPanel />

              <div className="projectHomeBody" key={`panels-${reloadKey}`}>
                {renderRoleHome()}
              </div>
            </div>
          </TabPane>

          {/* Site Visit Summary is not a home tab any more — it has its own
              page in the sidebar (/project/site-visit-summary), which is where
              the ~40-column table belongs. */}

          <TabPane tab="TPM Summary" key="tpm-summary">
            <div className="insideTab">
              <TpmSummary />
            </div>
          </TabPane>

          {!isGuestShare && (
            <TabPane tab="IP Summary" key="ip-summary">
              <div className="insideTab">
                <IpSummary />
              </div>
            </TabPane>
          )}

          <TabPane tab="Visit Data Summary" key="visit-data-summary">
            <div className="insideTab">
              <VisitDataSummary
                countryOfProjectId={projectData?.projectDetails?.country?.id}
                fetchProjectData={fetchProjectData}
                projectData={projectData}
              />
            </div>
          </TabPane>
        </Tabs>
      </div>

      {/* Modals for quick actions + project edit/preview. */}
      <AddSiteVisitCardModal
        visible={isModalVisibleSiteVisit}
        onOk={() => setIsModalVisibleSiteVisit(false)}
        onCancel={handleCancelModals}
        setMessage={setMsg}
      />
      <AddNewTaskModal
        setMessage={setMsg}
        visible={isModalVisibleAddTask}
        setIsModalVisible={handleCancelModals}
        updateData={() => {}}
      />
      <AddRedFlagCardModal
        visible={isModalVisibleAddFlag}
        onClose={handleCancelModals}
        setMessage={setMsg}
        fetchRedFlags={handleCancelModals}
        fetchDraftData={() => {}}
        setFetchDraftData={() => {}}
      />
      <AddIpPlan
        visible={isModalVisibleAddModal}
        onClose={() => setIsModalVisibleAddModal(false)}
        setMessage={setMsg}
        setFetchDraftData={() => {}}
        onAdd={() => {}}
      />
      <EditProject
        visible={showEditProjectModal}
        onClose={() => setShowEditProjectModal(false)}
        projectId={projectID}
        onSubmit={fetchProjectData}
        setMessage={setMsg}
      />
      <PreviewProject
        visible={showPreviewProjectModal}
        onClose={() => setShowPreviewProjectModal(false)}
        project={projectData}
        loading={loadingProject || (!projectData && showPreviewProjectModal)}
      />
      <StalledThresholdsModal
        open={showThresholdsModal}
        onClose={() => setShowThresholdsModal(false)}
        notify={notify}
      />
    </div>
  );
};

export default ProjectHome;
